import React, {useEffect, useState} from 'react';
import {Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, IconButton, TextField, Tooltip} from "@mui/material";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import Grid from "@mui/material/Grid";
import {Button, ControlledTextField} from "../../../components/forms";
import {ControlledAutocomplete} from "../../../components/forms/ControlledAutocomplete";
import {DumpTruckInsertDTO, DumpTruckViewDTO, schemaDumpTruckInsert} from "../types/dto";
import {DumpTruckFields} from "../types/fields";
import {DumpTruckStatusTypes, DumpTruckTypeTypes} from "../types/enum";
import {HttpDumpTrucks} from "../api/HttpDumpTrucks";
import {ControlledDatePicker} from 'src/components/forms/ControlledDatePicker';
import {EntityWithIdFields} from "../../../types";
import {differenceInCalendarDays} from "date-fns";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

type DialogMode = 'create' | 'edit' | 'view';

interface DumpTruckDialogProps extends DialogProps {
    onClose: () => void;
    onAfterSubmit: () => void;
    dumpTruck?: DumpTruckViewDTO;
}

function DumpTruckDialog({onClose, onAfterSubmit, dumpTruck, ...dialogProps}: DumpTruckDialogProps) {
    const [mode, setMode] = useState<DialogMode>(() => dumpTruck ? 'view' : 'create');
    const isViewMode = mode === 'view';

    const {control, handleSubmit, setValue, watch, setFocus, reset} = useForm<DumpTruckInsertDTO>({
        resolver: zodResolver(schemaDumpTruckInsert),

        //@ts-ignore
        defaultValues: {
            [DumpTruckFields.Client]: '',
            [DumpTruckFields.Address]: '',
            [DumpTruckFields.Invoice]: '',
            [DumpTruckFields.StatusCode]: DumpTruckStatusTypes.ACTIVE,
            [DumpTruckFields.TypeCode]: DumpTruckTypeTypes.REMOVAL,
            [DumpTruckFields.DeliveryNote]: '',
            [DumpTruckFields.Observations]: '',
            [DumpTruckFields.Quantity]: 1,
            [DumpTruckFields.DeliveryDate]: new Date(),
            [DumpTruckFields.RemovalDate]: dumpTruck?.[DumpTruckFields.RemovalDate] ?? null,
            ...dumpTruck,
            [DumpTruckFields.Amount]: dumpTruck?.[DumpTruckFields.Amount]?.toString(),
        }
    });

    const handleModeChange = (newMode: DialogMode) => {
        setMode(newMode);
        if (newMode === 'view') {
            // para que no queden cambios de otro volquete
            reset({
                ...dumpTruck,
                [DumpTruckFields.Amount]: dumpTruck?.[DumpTruckFields.Amount]?.toString(),
            });
        }
    };

    const deliveryDate = watch(DumpTruckFields.DeliveryDate);
    const removalDate = watch(DumpTruckFields.RemovalDate);
    const [dumpTruckDays, setDumpTruckDays] = useState<number>();

    useEffect(() => {
        if (deliveryDate && removalDate) {
            try {
                const diff = differenceInCalendarDays(new Date(removalDate), new Date(deliveryDate)) + 1;
                setDumpTruckDays(diff >= 0 ? diff : 0);
            } catch (error) {
                setDumpTruckDays(0);
            }
        } else {
            setDumpTruckDays(0);
        }
    }, [deliveryDate, removalDate]);

    useEffect(() => {
        if (mode === 'create') {
            const timer = setTimeout(() => setFocus(DumpTruckFields.Client), 10);

            return () => clearTimeout(timer);
        }
    }, [mode, setFocus]);

    const submitDumpTruck = (data: DumpTruckInsertDTO) => {
        //@ts-ignore
        return dumpTruck ? HttpDumpTrucks.put(dumpTruck[EntityWithIdFields.Id], data) : HttpDumpTrucks.post(data);
    };

    const onSubmitWrapper = () => {
        let mensajeError = 'No se pudo guardar el volquete';
        const promise = new Promise<void>((resolve, reject) => {
            handleSubmit(
                (formData) => {
                    submitDumpTruck(formData)
                        .then((response) => {
                            if (response?.tieneError) {
                                mensajeError = response.descripcionError;
                                reject(response.descripcionError);
                            } else {
                                setTimeout(onAfterSubmit, 500);
                                resolve();
                            }
                        })
                        .catch(reject);
                },
                (validationErrors) => {
                    mensajeError = 'Revise el formulario';
                    reject('Errores de validación');
                }
            )();
        });

        toast.promise(promise, {
            success: 'Guardado',
            error: () => mensajeError,
            loading: 'Guardando'
        });

        return promise;
    };

    useEffect(() => {
        // Solo setea la fecha si el status es UNLOADED y no hay ya una fecha
        const subscription = watch((value, {name}) => {
            if (name === DumpTruckFields.StatusCode && value[name] === DumpTruckStatusTypes.UNLOADED && !value[DumpTruckFields.RemovalDate]) {
                setValue(DumpTruckFields.RemovalDate, new Date());
            }
        });

        return () => subscription.unsubscribe();
    }, [watch, setValue]);

    const getTitle = () => {
        if (mode === 'create') return 'Nuevo Volquete';
        if (mode === 'edit') return 'Editar Volquete';

        return 'Detalle de Volquete';
    }

    return (
        <Dialog {...dialogProps} onClose={onClose} fullWidth>
            <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                {getTitle()}
                {dumpTruck && (
                    isViewMode ? (
                        <Tooltip title="Editar">
                            <IconButton
                                color={'primary'}
                                onClick={() => handleModeChange('edit')}>
                                <EditIcon/>
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Ver">
                            <IconButton
                                color={'primary'}
                                onClick={() => handleModeChange('view')}>
                                <VisibilityIcon/>
                            </IconButton>
                        </Tooltip>
                    )
                )}
            </DialogTitle>
            <DialogContent sx={{p: 4, mt: 2}}>
                <Grid container spacing={4} sx={{mt: 0.5}}>
                    <Grid item xs={12} sm={6}>
                        <ControlledTextField control={control} name={DumpTruckFields.Client} label={'Cliente'}
                                             fullWidth disabled={isViewMode}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ControlledTextField control={control} name={DumpTruckFields.Address} label={'Dirección'}
                                             fullWidth disabled={isViewMode}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ControlledAutocomplete loadOptions={HttpDumpTrucks.getTypes} control={control}
                                                name={DumpTruckFields.TypeCode} label={'Tipo'} valueField={'id'}
                                                disabled={isViewMode}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ControlledAutocomplete loadOptions={HttpDumpTrucks.getStatusTypes} control={control}
                                                name={DumpTruckFields.StatusCode} label={'Estado'} valueField={'id'}
                                                disabled={isViewMode}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <ControlledTextField control={control} name={DumpTruckFields.Amount} label={'Importe'}
                                             fullWidth currency prefix={'$'} disabled={isViewMode}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <ControlledTextField control={control} name={DumpTruckFields.DeliveryNote}
                                             label={'Nro. Remito'} fullWidth disabled={isViewMode}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <ControlledTextField control={control} name={DumpTruckFields.Invoice}
                                             label={'Nro. Factura'} fullWidth disabled={isViewMode}/>
                    </Grid>
                    {mode === 'create' &&
                        <Grid item xs={12} sm={2}>
                            <ControlledTextField control={control} name={DumpTruckFields.Quantity} label={'Cantidad'}
                                                 fullWidth type="number"/>
                        </Grid>
                    }
                    <Grid item xs={12} sm={4}>
                        <ControlledDatePicker control={control} name={DumpTruckFields.DeliveryDate}
                                              label="Fecha de Entrega" disabled={isViewMode}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <ControlledDatePicker control={control} name={DumpTruckFields.RemovalDate}
                                              label="Fecha de Retiro" disabled={isViewMode}/>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextField label={'Días'} value={dumpTruckDays || '-'} disabled variant="outlined"
                                   size="small"/>
                    </Grid>
                    <Grid item xs={12}>
                        <ControlledTextField control={control} name={DumpTruckFields.Observations}
                                             label={'Observaciones'} fullWidth multiline rows={4} disabled={isViewMode}/>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{p: (theme) => theme.spacing(2, 5), pb: 4}}>
                {isViewMode ? (
                    <Button variant='text' color='primary' onClick={onClose}>
                        Cerrar
                    </Button>
                ) : (
                    <>
                        <Button variant='text' color='secondary' onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button variant='contained' color='primary' promise={onSubmitWrapper}>
                            Guardar
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default DumpTruckDialog;
